import { FC } from 'react';

import { Tooltip } from '@/components/UI/Tooltip/Tooltip';
import VoorraadDots from '@/views/ddp/components/VoorraadBeheer/VoorraadDots';
import { getVoorraadSeverity } from '@/views/ddp/components/VoorraadBeheer/voorraadSeverity';

import { LmmTijdstip, LmmVerrijktV2 } from '../../types/LmmVoorraad';
import { getLmmTooltipTijdstipTitel } from '../../utils/lmmTijdstippen';
import { getLmmClaimsTotaal } from '../../utils/lmmVoorraad';

import styles from './LmmOverTekortTooltip.module.css';

type LmmOverTekortTooltipProps = {
  voorraad: LmmVerrijktV2;
  tijdstip?: LmmTijdstip;
  compact?: boolean;
};

const LmmOverTekortTooltip: FC<LmmOverTekortTooltipProps> = ({ voorraad, tijdstip, compact = false }) => {
  const claimsCount = getLmmClaimsTotaal(voorraad.benodigd);
  const variant = getVoorraadSeverity(voorraad.overOfTekort, claimsCount);
  const benodigd = voorraad.benodigd.dienst + claimsCount;
  const beschikbareClaims = variant === 'warning' ? voorraad.overOfTekort : -claimsCount;
  const beschikbareReizigersdienst =
    voorraad.brutoPark - voorraad.benodigd.dienst - voorraad.nietInzetbaar.aantalEenheden;
  const showBeschikbaarClaims = variant === 'warning' || (variant === 'error' && claimsCount > 0);
  const content = (
    <div className={styles.tooltipContent}>
      <header className={styles.tooltipHeader}>
        <strong>{voorraad.materieel}</strong>
        {tijdstip && <span>{getLmmTooltipTijdstipTitel(tijdstip)}</span>}
      </header>
      <div className={styles.tooltipBody}>
        <div className={styles.tooltipRow}>
          <span className={styles.label}>Bruto park:</span>
          <span className={styles.value}>{voorraad.brutoPark}</span>
        </div>
        <div className={styles.tooltipRow}>
          <span className={styles.label}>Benodigd:</span>
          <span className={styles.value}>{benodigd}</span>
        </div>
        <div className={styles.tooltipSubSection}>
          <div className={styles.tooltipSubRow}>
            <span className={styles.label}>Reizigersdienst:</span>
            <span className={styles.value}>{voorraad.benodigd.dienst}</span>
          </div>
          {voorraad.benodigd.gdst > 0 && (
            <div className={styles.tooltipSubRow}>
              <span className={styles.label}>GDST:</span>
              <span className={styles.value}>{voorraad.benodigd.gdst}</span>
            </div>
          )}
          {voorraad.benodigd.ebksb > 0 && (
            <div className={styles.tooltipSubRow}>
              <span className={styles.label}>EBKSB:</span>
              <span className={styles.value}>{voorraad.benodigd.ebksb}</span>
            </div>
          )}
        </div>
        <div className={styles.tooltipRow}>
          <span className={styles.label}>Niet inzetbaar:</span>
          <span className={styles.value}>{voorraad.nietInzetbaar.aantalEenheden}</span>
        </div>
        <div className={styles.availabilitySection}>
          {showBeschikbaarClaims && (
            <div className={[styles.tooltipRow, styles.availableClaims].join(' ')}>
              <span className={styles.label}>Beschikb. claims:</span>
              <span className={styles.value}>{beschikbareClaims}</span>
            </div>
          )}
          {variant === 'error' && (
            <div className={[styles.tooltipRow, styles.availablePassengerService].join(' ')}>
              <span className={styles.label}>Beschikb. r.dienst:</span>
              <span className={styles.value}>{beschikbareReizigersdienst}</span>
            </div>
          )}
          <div className={[styles.tooltipTotalRow, styles.availableTotal].join(' ')}>
            <span className={styles.label}>Beschikb. totaal:</span>
            <span className={styles.value}>{voorraad.overOfTekort}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Tooltip content={content} side='bottom' align='center' sideOffset={12} hideTooltip={voorraad.nvt}>
      <span className={styles.overTekortTooltipTrigger}>
        <VoorraadDots
          aantal={voorraad.overOfTekort}
          claimsCount={claimsCount}
          beschikbareReizigersdienst={beschikbareReizigersdienst}
          nvt={voorraad.nvt}
          compact={compact}
        />
      </span>
    </Tooltip>
  );
};

export default LmmOverTekortTooltip;
